import { Suspense } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function IndustriesPage() {
  const industries = [
    {
      title: 'Residential',
      description: 'Protect your home and family with comprehensive security solutions designed for modern living.',
      icon: 'ri-home-4-line',
      features: [
        'Smart home integration',
        'Video doorbell systems',
        'Whole-home surveillance',
        'Garage and gate access',
      ],
      image: 'https://public.readdy.ai/ai/img_res/cd2ab532501f31e15d1de53ed2b5382f.jpg',
    },
    {
      title: 'Commercial Office',
      description: 'Secure your workplace with enterprise-grade systems that protect assets and employees.',
      icon: 'ri-building-line',
      features: [
        'Multi-floor access control',
        'Employee badge systems',
        'Conference room security',
        'Parking lot surveillance',
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20commercial%20office%20building%20with%20professional%20security%20system%20installation%2C%20glass%20facade%2C%20access%20control%20panels%20at%20entrance%2C%20IP%20cameras%20visible%2C%20corporate%20architecture%2C%20daytime%20photography%2C%20business%20district%20setting%2C%20realistic%20style&width=800&height=600&seq=industry-office-1&orientation=landscape',
    },
    {
      title: 'Retail',
      description: 'Prevent theft and monitor customer flow with intelligent retail security solutions.',
      icon: 'ri-store-2-line',
      features: [
        'Point-of-sale monitoring',
        'Inventory protection',
        'Customer analytics',
        'Loss prevention systems',
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20retail%20store%20interior%20with%20security%20cameras%20and%20monitoring%20systems%2C%20clean%20shopping%20environment%2C%20professional%20surveillance%20equipment%2C%20bright%20lighting%2C%20contemporary%20retail%20design%2C%20customers%20shopping%2C%20realistic%20photography%2C%20high%20detail&width=800&height=600&seq=industry-retail-1&orientation=landscape',
    },
    {
      title: 'Healthcare',
      description: 'HIPAA-compliant security systems that protect patients, staff, and sensitive data.',
      icon: 'ri-hospital-line',
      features: [
        'Patient area monitoring',
        'Pharmacy security',
        'Staff access control',
        'Emergency response integration',
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20healthcare%20facility%20entrance%20with%20security%20systems%2C%20hospital%20building%20exterior%2C%20access%20control%20panels%2C%20professional%20medical%20environment%2C%20clean%20architecture%2C%20security%20cameras%20visible%2C%20daytime%20shot%2C%20realistic%20photography%20style&width=800&height=600&seq=industry-healthcare-1&orientation=landscape',
    },
    {
      title: 'Education',
      description: 'Create safe learning environments with comprehensive campus security solutions.',
      icon: 'ri-school-line',
      features: [
        'Campus-wide surveillance',
        'Visitor management',
        'Emergency lockdown systems',
        'Parking lot security',
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20school%20campus%20with%20security%20systems%2C%20educational%20building%20exterior%2C%20security%20cameras%20and%20access%20control%20visible%2C%20safe%20learning%20environment%2C%20students%20walking%2C%20contemporary%20architecture%2C%20daytime%20photography%2C%20realistic%20style&width=800&height=600&seq=industry-education-1&orientation=landscape',
    },
    {
      title: 'Industrial',
      description: 'Heavy-duty security systems designed for warehouses, factories, and industrial facilities.',
      icon: 'ri-factory-line',
      features: [
        'Perimeter protection',
        'Loading dock monitoring',
        'Equipment area security',
        'After-hours surveillance',
      ],
      image: 'https://readdy.ai/api/search-image?query=Industrial%20warehouse%20facility%20with%20professional%20security%20system%20installation%2C%20large%20commercial%20building%2C%20security%20cameras%20and%20access%20control%2C%20loading%20docks%2C%20industrial%20architecture%2C%20professional%20photography%2C%20realistic%20detailed%20style&width=800&height=600&seq=industry-industrial-1&orientation=landscape',
    },
  ];

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="bg-black min-h-screen">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center">
              <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-6">
                Industries We Serve
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Tailored security solutions for every sector across the Bay Area
              </p>
            </div>
          </div>
        </section>

        {/* Industries Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="space-y-20">
              {industries.map((industry, index) => (
                <div
                  key={index}
                  className={`grid md:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                    <div className="w-16 h-16 bg-coral-100 rounded-2xl flex items-center justify-center mb-6">
                      <i className={`${industry.icon} text-3xl text-coral-600`}></i>
                    </div>
                    <h2 className="font-serif text-4xl font-bold text-slate-900 mb-4">
                      {industry.title}
                    </h2>
                    <p className="text-lg text-slate-600 mb-6">
                      {industry.description}
                    </p>
                    <ul className="space-y-3 mb-8">
                      {industry.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center">
                          <i className="ri-check-line text-xl text-coral-600 mr-3"></i>
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => window.REACT_APP_NAVIGATE('/contact')}
                      className="inline-flex items-center px-6 py-3 bg-coral-600 text-white rounded-full font-semibold hover:bg-coral-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                    >
                      Learn More
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  </div>
                  <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                    <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-2xl">
                      <img
                        src={industry.image}
                        alt={industry.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl font-bold text-slate-900 mb-4">
                Trusted Across the Bay Area
              </h2>
              <p className="text-xl text-slate-600">
                Our experience speaks for itself
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-coral-600 mb-2">500+</div>
                <div className="text-slate-600 font-medium">Projects Completed</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-coral-600 mb-2">15+</div>
                <div className="text-slate-600 font-medium">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-coral-600 mb-2">98%</div>
                <div className="text-slate-600 font-medium">Client Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-coral-600 mb-2">24/7</div>
                <div className="text-slate-600 font-medium">Support Available</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-coral-600 to-coral-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Protect Your Business?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Let us design a custom security solution for your industry
            </p>
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/contact')}
              className="inline-flex items-center px-8 py-4 bg-white text-coral-600 rounded-full font-semibold text-lg hover:bg-slate-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
            >
              Get Started Today
              <i className="ri-arrow-right-line ml-2 text-xl"></i>
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
